// The email sent when a finalized radicado is annulled.
//
// Shared on purpose: handleCorrespondence requires it to send the notice, and
// the radicados config screen imports it for the preview, so what an admin
// previews is exactly what goes out. It lives inside the function's deploy
// source, so the function needs no copy of it. Keep it free of dependencies —
// it runs in Node and in the browser.
//
// Styled like the PQRSD emails (pqrsd/_shared/emailTemplates.js: colored header
// with the logo, tinted title band, white card, muted footer), which is where
// the institution's emails are heading; the older radicado emails in
// handleCorrespondence/index.js are plain <font> markup. The brand is passed in
// (config/global brandColor / brandName / logoUrl / logoWidth) rather than set
// on module state, so the preview and a send can never leak into each other.
//
// Every value is escaped: the motive is free text typed by whoever annulled.
// Who annulled is deliberately left out, the same way the public QR page
// leaves it out.

// Who a notice is addressed to, in the order addresses are collected. "citizen"
// is the person who filed a PQRSD: they get the PQRSD email (their tracking
// page, PQRSD wording) instead of the radicado one.
const CANCEL_NOTICE_AUDIENCES = ["citizen", "recipients", "carbonCopy", "senders"];

const CANCEL_NOTICE_AUDIENCE_LABELS = {
  citizen: "Ciudadano (PQRSD)",
  recipients: "Destinatario",
  carbonCopy: "Copia",
  senders: "Remitente",
};

// Which switch in config/filed.cancelNotifications turns each audience on.
const CANCEL_NOTICE_AUDIENCE_SWITCH = {
  citizen: "pqrsdCitizen",
  recipients: "recipients",
  carbonCopy: "carbonCopy",
  senders: "senders",
};

const CANCEL_NOTICE_MAX = 100;
const EMAIL_SHAPE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

const normalizeEmail = (raw) => (typeof raw === "string" ? raw.trim().toLowerCase() : "");

// The address a PQRSD's citizen is written to: the one on the PQRSD, else the
// radicado's recipient (intake puts the citizen there). null when neither is a
// usable address — an anonymous PQRSD filed without one.
function pqrsdCitizenEmail(filed) {
  if (!filed || filed.filedType !== "pqrsd") return null;
  const candidates = [filed.pqrsd && filed.pqrsd.email]
    .concat((Array.isArray(filed.recipients) ? filed.recipients : []).map((r) => r && r.email));
  const email = candidates.map(normalizeEmail).find((e) => e && EMAIL_SHAPE.test(e));
  return email || null;
}

// Everyone a notice goes to, as [{ audience, email, person }], for the switches
// on: the PQRSD citizen, then recipients, copies and senders. An address listed
// twice gets one email, as whoever it appeared as first. A PQRSD's citizen is
// only ever written to as the citizen — with that switch off they get nothing,
// not the radicado email through the recipients list.
function cancelNoticeAddressees(filed, notices) {
  const seen = new Set();
  const out = [];
  const add = (audience, rawEmail, person) => {
    const email = normalizeEmail(rawEmail);
    if (!email || !EMAIL_SHAPE.test(email) || seen.has(email)) return;
    seen.add(email);
    out.push({ audience, email, person });
  };
  const citizen = pqrsdCitizenEmail(filed);
  if (citizen) {
    if (notices.pqrsdCitizen) {
      const solicitor = (filed.pqrsd && filed.pqrsd.solicitor) || {};
      const anonymous = !!(filed.pqrsd && filed.pqrsd.anonymous);
      add("citizen", citizen, anonymous ? null : { name: solicitor.name || "", lastName: solicitor.surname || "" });
    }
    seen.add(citizen);
  }
  ["recipients", "carbonCopy", "senders"].forEach((audience) => {
    if (!notices[audience]) return;
    const list = Array.isArray(filed[audience]) ? filed[audience] : [];
    list.forEach((entry) => {
      if (audience === "carbonCopy") add(audience, entry, null);
      else if (entry && typeof entry === "object") add(audience, entry.email, entry);
    });
  });
  return out.slice(0, CANCEL_NOTICE_MAX);
}

// What annulling this radicado would email, before doing it — the same decision
// the send makes, so the dialog can say it: { status, addressees, includeReason }.
// status is not_applicable (never finalized: nobody was sent it), disabled,
// no_addresses or will_send.
function planCancelNotice(filed, notices, previousStatus = filed && filed.status) {
  if (previousStatus !== "DONE") return { status: "not_applicable", addressees: [], includeReason: false };
  if (!notices || !notices.enabled) return { status: "disabled", addressees: [], includeReason: false };
  const addressees = cancelNoticeAddressees(filed, notices);
  return {
    status: addressees.length ? "will_send" : "no_addresses",
    addressees,
    includeReason: !!notices.includeReason,
  };
}

const DEFAULT_BRAND_COLOR = "#00652e";
const DEFAULT_BRAND_NAME = "Comunicaciones oficiales";
const DEFAULT_LOGO_WIDTH = 140;
const ALERT_COLOR = "#c62828";

// Every piece of text in the notice is editable in the radicados config
// (config/filed.cancelNotifications.texts); the layout — greeting, details
// table, motive box, button, brand — is not. Texts take placeholders, filled in
// per email. A required text left empty falls back to its default; an optional
// one left empty is removed from the email.
const CANCEL_NOTICE_PLACEHOLDERS = [
  { key: "radicado", label: "Número de radicado", help: "El número del radicado, en negrita. Ej.: CI_20260914-00042" },
  { key: "asunto", label: "Asunto", help: "El asunto del radicado." },
  { key: "relacion", label: "Relación con el radicado", help: "“que le fue enviado”, “del que usted recibió copia”, “que usted radicó” o, para el ciudadano, “que usted presentó”." },
  { key: "fecha_radicacion", label: "Fecha de radicación", help: "Cuándo se radicó. Ej.: 11 de septiembre de 2026 a las 6:55 p. m." },
  { key: "fecha_anulacion", label: "Fecha de anulación", help: "Cuándo se anuló." },
];

const CANCEL_NOTICE_TEXT_FIELDS = [
  { key: "subject", label: "Asunto del correo", max: 200, required: true, multiline: false },
  { key: "title", label: "Título", max: 120, required: true, multiline: false },
  { key: "intro", label: "Mensaje principal", max: 1000, required: true, multiline: true },
  { key: "button", label: "Texto del botón", max: 60, required: true, multiline: false },
  { key: "closing", label: "Nota final", max: 500, required: false, multiline: true },
  { key: "contact", label: "Contacto", max: 500, required: false, multiline: true },
  { key: "footer", label: "Pie del correo", max: 300, required: false, multiline: true },
];

// The PQRSD citizen's email. Same fields and placeholders; {relacion} reads
// "que usted presentó".
const DEFAULT_PQRSD_CANCEL_NOTICE_TEXTS = {
  subject: "Su PQRSD {radicado} fue anulada",
  title: "Su PQRSD fue anulada",
  intro: "Le informamos que su PQRSD con radicado {radicado} fue anulada. "
    + "No es necesario que realice ningún trámite adicional. Si aún necesita presentar su solicitud, puede radicar una nueva PQRSD.",
  button: "Ver el estado de mi PQRSD",
  closing: "La solicitud se conserva en el archivo institucional marcada como anulada.",
  contact: "Si tiene alguna duda, comuníquese con la oficina de Archivo Central en archivo@tdea.edu.co.",
  footer: "Este es un mensaje automático del sistema PQRSD. Por favor no responda a este correo.",
};

const DEFAULT_CANCEL_NOTICE_TEXTS = {
  subject: "Radicado anulado — No. {radicado}",
  title: "Radicado anulado",
  intro: "Le informamos que el radicado {radicado}, {relacion}, fue anulado. "
    + "El documento ya no tiene validez y no requiere ningún trámite de su parte.",
  button: "Ver el radicado",
  closing: "El radicado se conserva en el archivo institucional marcado como anulado.",
  contact: "Si tiene alguna duda, comuníquese con la oficina de Archivo Central en archivo@tdea.edu.co.",
  footer: "Este es un mensaje automático de comunicaciones oficiales. Por favor no responda a este correo.",
};

// Stored texts → the texts to render. A string is trimmed and capped; anything
// else (absent, null, a number) is the default, and so is an empty required one.
function normalizeCancelNoticeTexts(stored, defaults = DEFAULT_CANCEL_NOTICE_TEXTS) {
  const source = stored && typeof stored === "object" ? stored : {};
  return CANCEL_NOTICE_TEXT_FIELDS.reduce((acc, { key, max, required }) => {
    const value = typeof source[key] === "string" ? source[key].trim().slice(0, max) : null;
    acc[key] = value === null || (required && !value) ? defaults[key] : value;
    return acc;
  }, {});
}

const EMAIL_IN_TEXT = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

// Plain single-line text (subject, title, button): placeholders filled, line
// breaks flattened — a subject is a mail header and must stay on one line.
function fillPlain(template, values) {
  return template
    .replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
    )
    .replace(/[\r\n]+/g, " ");
}

// HTML for a paragraph: the admin's text escaped, then placeholders filled with
// escaped values (the number in bold), then email addresses turned into mailto
// links. Escaping first means neither the placeholders nor the address pattern
// can ever meet markup the admin typed.
function fillHtml(template, values, linkColor = "#666") {
  return esc(template)
    .replace(/\{(\w+)\}/g, (match, key) => {
      if (!Object.prototype.hasOwnProperty.call(values, key)) return match;
      const value = esc(values[key]);
      return key === "radicado" ? `<strong>${value}</strong>` : value;
    })
    .replace(EMAIL_IN_TEXT, (email) => `<a href="mailto:${email}" style="color:${linkColor};">${email}</a>`);
}

function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Firestore Timestamp (admin or web SDK), Date, ISO string or millis.
function toDate(value) {
  if (!value) return null;
  const date = typeof value.toDate === "function"
    ? value.toDate()
    : value instanceof Date
      ? value
      : typeof value === "object" && typeof value._seconds === "number"
        ? new Date(value._seconds * 1000)
        : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date) return null;
  try {
    return date.toLocaleString("es-CO", {
      timeZone: "America/Bogota",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return date.toISOString();
  }
}

// The base for links in the notice from config/global.publicBaseUrl, trimmed of
// trailing slashes; null when unset or not an http(s) URL. The function and the
// config preview both go through this.
function cancelNoticeBaseUrl(publicBaseUrl) {
  const raw = String(publicBaseUrl || "").trim();
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return `${url.origin}${url.pathname}`.replace(/\/+$/, "");
  } catch (_) {
    return null;
  }
}

// config/global → the brand the shell renders, with the same fallbacks the
// PQRSD templates use. Anything malformed falls back instead of breaking the
// markup (a color that isn't #rrggbb, a logo that isn't http(s)).
function resolveBrand(brand = {}) {
  const color = /^#[0-9a-fA-F]{6}$/.test(brand.color || "") ? brand.color : DEFAULT_BRAND_COLOR;
  const logo = /^https?:\/\//i.test(brand.logo || "") ? brand.logo : "";
  const width = Number(brand.logoWidth) || DEFAULT_LOGO_WIDTH;
  return {
    color,
    name: String(brand.name || brand.orgName || DEFAULT_BRAND_NAME),
    logo,
    logoWidth: Math.min(Math.max(width, 40), 320),
  };
}

// Blend a #rrggbb color toward white, for the soft title band.
function tint(hex, amount = 0.88) {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return "#eef5f0";
  const n = parseInt(m[1], 16);
  return "#" + [(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map((c) => Math.round(c + (255 - c) * amount).toString(16).padStart(2, "0"))
    .join("");
}

// Same card as pqrsd/_shared/emailTemplates.js shell(), with a radicados footer.
function shell(brand, title, bodyHtml, footerHtml, systemName = "Comunicaciones oficiales") {
  const name = esc(brand.name);
  const logoCell = brand.logo
    ? `<td align="right" style="vertical-align:middle;"><img src="${esc(brand.logo)}" alt="${name}" width="${brand.logoWidth}" style="display:inline-block;width:${brand.logoWidth}px;max-width:100%;height:auto;border:0;"></td>`
    : "";
  return `<!doctype html><html><body style="margin:0;padding:0;background:#f4f5f7;">
<div style="font-family:Arial,Helvetica,sans-serif;line-height:1.5;color:#333;max-width:600px;margin:0 auto;padding:24px 12px;">
  <div style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
    <div style="background:${brand.color};padding:22px 32px;">
      <table role="presentation" width="100%" style="border-collapse:collapse;"><tr>
        <td style="vertical-align:middle;"><span style="color:#ffffff;font-size:18px;font-weight:700;">${name}</span></td>
        ${logoCell}
      </tr></table>
    </div>
    <div style="background:${tint(brand.color)};padding:20px 32px;">
      <h1 style="margin:0;color:${brand.color};font-size:22px;font-weight:700;">${esc(title)}</h1>
    </div>
    <div style="padding:28px 32px;font-size:15px;color:#333;">
      ${bodyHtml}
      ${footerHtml ? `<p style="font-size:12px;color:#9aa0a6;margin:28px 0 0;white-space:pre-wrap;">${footerHtml}</p>` : ""}
    </div>
  </div>
  <p style="text-align:center;color:#9aa0a6;font-size:12px;margin:18px 0 0;">${name} · ${esc(systemName)}</p>
</div>
</body></html>`;
}

function row(label, valueHtml) {
  return `<tr><td style="padding:6px 16px 6px 0;color:#666;vertical-align:top;white-space:nowrap;">${esc(label)}</td><td style="padding:6px 0;">${valueHtml}</td></tr>`;
}

function fullName(person) {
  if (!person) return "";
  return `${person.name || ""} ${person.lastName || ""}`.trim();
}

/**
 * @param {object} args
 * @param {object} args.filed         the radicado (filedId, subject, createdAt/doneAt)
 * @param {object} args.cancellation  { reason, cancelledAt }
 * @param {string} args.audience      one of CANCEL_NOTICE_AUDIENCES
 * @param {object} [args.person]      the recipient or sender, for the greeting
 * @param {string} [args.filedUrl]    link to the radicado's view
 * @param {boolean} [args.includeReason]
 * @param {object} [args.brand]       config/global { color, name, orgName, logo, logoWidth }
 * @param {object} [args.texts]       config/filed cancelNotifications.texts (see CANCEL_NOTICE_TEXT_FIELDS)
 * @param {object} [args.pqrsdTexts]  cancelNotifications.pqrsdTexts, for the "citizen" audience
 * @returns {{ subject: string, html: string }}
 */
function buildCancelNoticeEmail({
  filed = {},
  cancellation = {},
  audience = "recipients",
  person = null,
  filedUrl = null,
  includeReason = true,
  brand = {},
  texts,
  pqrsdTexts,
} = {}) {
  const resolved = resolveBrand(brand);
  const isCitizen = audience === "citizen";
  const copy = isCitizen
    ? normalizeCancelNoticeTexts(pqrsdTexts, DEFAULT_PQRSD_CANCEL_NOTICE_TEXTS)
    : normalizeCancelNoticeTexts(texts);
  const filedId = filed.filedId || "";

  const name = audience === "carbonCopy" ? "" : fullName(person);
  const greeting = name
    ? `<p style="margin:0 0 16px;">${person && person.usertitle ? `${esc(person.usertitle)} ` : "Estimado(a) "}<strong>${esc(name)}</strong>:</p>`
    : `<p style="margin:0 0 16px;">Estimado(a) ${isCitizen ? "ciudadano(a)" : "usuario(a)"}:</p>`;

  const why = {
    recipients: "que le fue enviado",
    carbonCopy: "del que usted recibió copia",
    senders: "que usted radicó",
    citizen: "que usted presentó",
  }[audience] || "que le fue enviado";

  const filedOn = formatDate(filed.doneAt || filed.createdAt);
  const cancelledOn = formatDate(cancellation.cancelledAt) || formatDate(new Date());
  const reason = typeof cancellation.reason === "string" ? cancellation.reason.trim() : "";
  const values = {
    radicado: filedId,
    asunto: filed.subject || "",
    relacion: why,
    fecha_radicacion: filedOn || "",
    fecha_anulacion: cancelledOn || "",
  };
  const subject = fillPlain(copy.subject, values);
  const paragraph = (text, style) => (text ? `<p style="${style}white-space:pre-wrap;">${fillHtml(text, values)}</p>` : "");

  const body = greeting +
    paragraph(copy.intro, "margin:0 0 16px;") +
    '<table role="presentation" style="border-collapse:collapse;margin:16px 0;">' +
      row("Radicado:", `<strong>${esc(filedId)}</strong>`) +
      (filed.subject ? row("Asunto:", esc(filed.subject)) : "") +
      (filedOn ? row("Radicado el:", esc(filedOn)) : "") +
      row("Anulado el:", esc(cancelledOn)) +
    "</table>" +
    (includeReason && reason
      ? `<div style="margin:16px 0;padding:14px 16px;background:#fdecea;border-left:4px solid ${ALERT_COLOR};border-radius:6px;">` +
          `<div style="font-size:12px;font-weight:700;color:${ALERT_COLOR};text-transform:uppercase;letter-spacing:0.04em;margin-bottom:4px;">Motivo de la anulación</div>` +
          `<div style="white-space:pre-wrap;color:#333;">${esc(reason)}</div>` +
        "</div>"
      : "") +
    (filedUrl
      ? `<p style="margin:20px 0 8px;"><a href="${esc(filedUrl)}" style="display:inline-block;padding:12px 22px;background:${resolved.color};color:#fff;text-decoration:none;border-radius:8px;font-weight:600;">${esc(fillPlain(copy.button, values))}</a></p>`
      : "") +
    paragraph(copy.closing, "font-size:12px;color:#666;margin:0 0 16px;") +
    paragraph(copy.contact, "font-size:13px;color:#666;margin:16px 0 0;");

  return {
    subject,
    html: shell(
      resolved,
      fillPlain(copy.title, values),
      body,
      copy.footer ? fillHtml(copy.footer, values, "#9aa0a6") : "",
      isCitizen ? "Sistema de PQRSD" : "Comunicaciones oficiales"
    ),
  };
}

module.exports = {
  CANCEL_NOTICE_AUDIENCES,
  CANCEL_NOTICE_AUDIENCE_LABELS,
  CANCEL_NOTICE_AUDIENCE_SWITCH,
  CANCEL_NOTICE_MAX,
  DEFAULT_PQRSD_CANCEL_NOTICE_TEXTS,
  cancelNoticeAddressees,
  planCancelNotice,
  pqrsdCitizenEmail,
  CANCEL_NOTICE_PLACEHOLDERS,
  CANCEL_NOTICE_TEXT_FIELDS,
  DEFAULT_CANCEL_NOTICE_TEXTS,
  buildCancelNoticeEmail,
  cancelNoticeBaseUrl,
  normalizeCancelNoticeTexts,
};
